import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SUPPORTED_LOCALES, type SupportedLocale } from './index';
import { useTranslation } from 'react-i18next';
import { safeSetItem } from '@/lib/storage';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

const LOCALE_LABELS: Record<SupportedLocale, string> = {
  en: 'EN',
  ru: 'RU',
  es: 'ES',
};

export const LanguageSwitcher: React.FC<{ className?: string }> = ({ className = '' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const currentLocale: SupportedLocale = location.pathname.startsWith('/ru')
    ? 'ru'
    : location.pathname.startsWith('/es')
    ? 'es'
    : 'en';

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const switchTo = (locale: SupportedLocale) => {
    if (locale === currentLocale) { setOpen(false); return; }
    i18n.changeLanguage(locale);
    safeSetItem('preferred_locale', locale);
    const pathWithoutLocale = location.pathname.replace(/^\/(ru|es)/, '') || '/';
    const newPath = locale === 'en'
      ? pathWithoutLocale
      : `/${locale}${pathWithoutLocale === '/' ? '' : pathWithoutLocale}`;
    navigate(newPath || '/');
    setOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-lg text-primary bg-primary/10 hover:bg-primary/15 transition-colors"
      >
        {LOCALE_LABELS[currentLocale]}
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -4 }}
            transition={{ duration: 0.12 }}
            className="absolute right-0 top-full mt-1 min-w-[80px] rounded-xl p-1 shadow-lg bg-card/95 backdrop-blur-xl border border-border/20 z-50"
          >
            {SUPPORTED_LOCALES.map((locale) => (
              <button
                key={locale}
                onClick={() => switchTo(locale)}
                className={`w-full text-left px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  locale === currentLocale
                    ? 'text-primary bg-primary/10'
                    : 'text-muted-foreground hover:text-foreground hover:bg-foreground/[0.04]'
                }`}
              >
                {t(`language_switcher.${locale}`)}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
